import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, Button, IconButton, Stack, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { DataTableSkeleton } from '@/components/common/DataTableSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadMoreButton } from '@/components/common/LoadMoreButton';
import { PageHeader } from '@/components/common/PageHeader';
import { useProfesionalesMap } from '@/features/establecimientos/hooks/useProfesionalesMap';
import { isApiError } from '@/lib/apiError';

import { ProtocolosFiltersBar } from '../components/ProtocolosFilters';
import { ProtocolosTable } from '../components/ProtocolosTable';
import { useEstablecimientosMap } from '../hooks/useEstablecimientosMap';
import { useProtocolosFilters } from '../hooks/useProtocolosFilters';
import { useProtocolos } from '../hooks/useProtocolos';

export function ProtocolosPage(): JSX.Element {
  const navigate = useNavigate();
  const { filters, updateFilters, clearFilters } = useProtocolosFilters();

  const {
    items,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch,
  } = useProtocolos({
    numero_protocolo: filters.numeroProtocolo || undefined,
    uid_profesional: filters.uidProfesional || undefined,
    uid_establecimiento: filters.uidEstablecimiento || undefined,
    sort_order: filters.sortOrder,
    limit: 50,
  });

  const { getName: getProfesionalName, isLoading: profesLoading } = useProfesionalesMap();
  const { map: establecimientosMap, isLoading: estabsLoading } = useEstablecimientosMap();

  const isInitialLoading = isLoading || profesLoading || estabsLoading;

  return (
    <>
      <PageHeader
        title="Protocolos"
        subtitle="Listado de protocolos del laboratorio"
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Recargar">
              <IconButton onClick={() => void refetch()} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/protocolos/nuevo')}
            >
              Nuevo protocolo
            </Button>
          </Stack>
        }
      />

      <ProtocolosFiltersBar
        filters={filters}
        onChange={updateFilters}
        onClear={clearFilters}
      />

      {isInitialLoading && <DataTableSkeleton columns={7} rows={5} />}

      {!isInitialLoading && isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => navigate(0)}>
              Reintentar
            </Button>
          }
        >
          {isApiError(error) ? error.message : 'No se pudieron cargar los protocolos.'}
        </Alert>
      )}

      {!isInitialLoading && !isError && items.length === 0 && (
        <EmptyState
          icon={<AssignmentIcon sx={{ fontSize: 'inherit' }} />}
          title="No hay protocolos para mostrar"
          description="Probá ajustar los filtros o crear un nuevo protocolo."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/protocolos/nuevo')}
            >
              Nuevo protocolo
            </Button>
          }
        />
      )}

      {!isInitialLoading && !isError && items.length > 0 && (
        <Box>
          <ProtocolosTable
            items={items}
            getProfesionalName={getProfesionalName}
            getEstablecimientoName={(uid) => establecimientosMap.get(uid) ?? uid}
            sortOrder={filters.sortOrder}
            onSortChange={(order) => updateFilters({ sortOrder: order })}
          />
          <Stack alignItems="center">
            <LoadMoreButton
              hasMore={hasNextPage}
              isLoading={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            />
          </Stack>
        </Box>
      )}
    </>
  );
}
